import { useState, useEffect, useRef } from 'react';
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel } from "@fluentui/react-components";
import riskStyle from "../../views/changeRequest/ChangeRequest.module.scss";
import {
  ListChecks as TaskIcon,
  CheckCircle as CheckCircleIcon,
  TriangleAlert,
  Milestone,
  CircleAlert,
  File,
  CloudUpload,
  TriangleAlertIcon,
  CircleCheck,
  FilePlus2
} from "lucide-react";
import Styles from './AiProjectCharter.module.scss';
import { api as projectService } from '../../store/services/projects.service';
import { api as searchService } from '../../store/services/search.service';
import Button from '../../components/controls/button';
import { useHistory } from 'react-router';
import ContactField from './components/ContactField';
import EditableField from './components/EditableField';
import ModalCheckbox from '../../components/controls/modalcheckbox/ModalCheckbox';
import ModalCheckboxWarning from '../../components/controls/modalcheckbox/ModalCheckboxWarning';
import { getRiskSeverityColorClass, getRiskSeverityStatus, getRiskStatus, getRiskType, riskCategoryOptions, riskImpact, riskProbability, RiskStatusOptions, scopePriorityOptions, ScopeStatus, taskPriorityOptions, taskStatusOptions } from '../../helpers/utils';
import { AiIcon } from '../../modules/default-svg';

const STEPS = {
  UPLOAD: 0,
  REVIEW: 1,
  METADATA: 2,
  PROCESSING: 3,
  COMPLETE: 4
};

// account settings
const settings = JSON.parse(localStorage.getItem("settings"));
const accountCurrencySymbol = settings?.regionalSettings?.currency?.split(" ")[1] || "$";

const AiProjectCharter = () => {
  const history = useHistory();
  const fileInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extracting, setExtracting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  // Charter data
  const [charterData, setCharterData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [dateWarnings, setDateWarnings] = useState([]);

  // Metadata
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [portfolioSearch, setPortfolioSearch] = useState('');
  const [programSearch, setProgramSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [portfolioResults, setPortfolioResults] = useState([]);
  const [programResults, setProgramResults] = useState([]);
  const [clientSearchError, setClientSearchError] = useState('');

  const [primeOptions, setPrimeOptions] = useState([]);

  // Load from session storage on mount
  useEffect(() => {
    const savedData = sessionStorage.getItem('aiCharterDraft');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.charterData) {
          setCharterData(parsed.charterData);
          setSelectedClient(parsed.selectedClient);
          setSelectedPortfolio(parsed.selectedPortfolio);
          setSelectedProgram(parsed.selectedProgram);
          setCurrentStep(STEPS.REVIEW);
        }
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, []);

  // Save to session storage
  useEffect(() => {
    if (charterData && currentStep === STEPS.REVIEW) {
      sessionStorage.setItem('aiCharterDraft', JSON.stringify({
        charterData,
        selectedClient,
        selectedPortfolio,
        selectedProgram
      }));
    }
  }, [charterData, selectedClient, selectedPortfolio, selectedProgram, currentStep]);

  // Debounced search effects
  useEffect(() => {
    searchService.Clients( clientSearch || "")
    .then(res => {
      const options = (res || []).map(client => ({
        label: client.name,
        value: client.id,
        image: client.image,
      }));
      setClientResults(options);
    })
    .catch(() => { });
  }, [clientSearch]);

  useEffect(() => {
    var params = {
      clientId: selectedClient,
    }
    searchService.Portfolios(portfolioSearch || "", params)
                  .then(res => {
                    const options = (res || []).map(portfolio => ({
                      label: portfolio.name,
                      value: portfolio.id,
                    }));
                    setPortfolioResults(options);
                  })
                  .catch(() => { });
  }, [portfolioSearch]);

  useEffect(() => {
    var params = {
      clientId: selectedClient,
      portfolioId: selectedPortfolio,
    }
    searchService.Programs(programSearch || "", params)
                .then(res => {
                  const options = (res || []).map(program => ({
                    label: program.name,
                    value: program.id,
                  }));
                  setProgramResults(options);
                })
                .catch(() => { });
  }, [programSearch]);

  const handlePrimeSearch = (term) => {
    searchService.Users(term).then(res => {
      const options = (res || []).map(prime => ({
        label: prime.name,
        value: prime.id,
        image: prime.image,
      }));
      setPrimeOptions(options);
    }).catch(() => { });
  }
  // Validate and check dates
  useEffect(() => {
    if (charterData) {
      const validation = projectService.validateCharterData(charterData);
      setValidationErrors(validation.errors);

      const warnings = projectService.checkDateHierarchy(
        charterData.project || {},
        charterData.scopes || [],
        charterData.tasks || []
      );
      setDateWarnings(warnings);
    }
  }, [charterData]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError(null);
    handleUpload(file);
  };

  const handleUpload = async (file) => {
    setExtracting(true);
    setUploadProgress(0);

    const progressInterval = setInterval(() => {
      setUploadProgress(prev => prev >= 90 ? 90 : prev + 10);
    }, 200);

    try {
      const response = await projectService.extractCharterFromPdf(file);
      if(response.scopes?.length > 0){
        response.scopes.forEach((scope) => {
          var tasks = response.tasks.filter(t => t.scopeTempId === scope.tempId);
          if(tasks.length > 0) {
            scope.progress = projectService.calculateScopeProgress(tasks);
          }
        });
        
        response.project.progress = projectService.calculateProjectProgress(response.scopes, response.tasks);
      }
      
      
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      setTimeout(() => {
        const responseData = response.data || response;

        const normalizedData = {
          project: responseData.project || {},
          scopes: Array.isArray(responseData.scopes) ? responseData.scopes : [],
          tasks: Array.isArray(responseData.tasks) ? responseData.tasks : [],
          risks: Array.isArray(responseData.risks) ? responseData.risks : []
        };

        setCharterData(normalizedData);
        setCurrentStep(STEPS.REVIEW);
        setExtracting(false);
      }, 500);
    } catch (err) {
      clearInterval(progressInterval);
      setError(err.response?.data?.message || err.message || 'Failed to extract charter data');
      setExtracting(false);
      setUploadProgress(0);
    }
  };

  const updateField = (entityType, id, field, value) => {
    const newData = JSON.parse(JSON.stringify(charterData));

    if (entityType === 'project') {
      newData.project[field] = value;
    } else if (entityType === 'scope') {
      const scopeIndex = newData.scopes.findIndex(s => s.tempId === id);
      if (scopeIndex !== -1) {
        newData.scopes[scopeIndex][field] = value;
      }
    } else if (entityType === 'task') {
      const taskIndex = newData.tasks.findIndex(t => t.tempId === id);
      if (taskIndex !== -1) {
        newData.tasks[taskIndex][field] = value;

        if (field === 'progress') {
          const task = newData.tasks[taskIndex];
          if (value >= 100) {
            newData.tasks[taskIndex].status = 3;
          }

          const scopeId = task.scopeTempId;
          const scopeIndex = newData.scopes.findIndex(s => s.tempId === scopeId);
          if (scopeIndex !== -1) {
            const scopeProgress = projectService.calculateScopeProgress(newData.tasks.filter(t => t.scopeTempId === scopeId));
            newData.scopes[scopeIndex].progress = scopeProgress;
            if (scopeProgress >= 100) {
              newData.scopes[scopeIndex].status = 3;
            }
          }

          newData.project.progress = projectService.calculateProjectProgress(newData.scopes, newData.tasks);
          if (newData.project.progress >= 100) {
            newData.project.status = 3;
          }
        }
      }
    } else if (entityType === 'risk') {
      const riskIndex = newData.risks.findIndex(r => r.tempId === id);
      if (riskIndex !== -1) {
        newData.risks[riskIndex][field] = value;

        if(field === 'probability' || field === 'impact') {
          const risk = newData.risks[riskIndex];
          newData.risks[riskIndex]['severity'] = parseInt(newData.risks[riskIndex]['probability']) * parseInt(newData.risks[riskIndex]['impact']);
        }
      }
    }

    setCharterData(newData);
  };

  const handleNext = () => {
    const validation = projectService.validateCharterData(charterData);
    if (!validation.isValid) {
      setError('Please fix validation errors before continuing');
      return;
    }
    setCurrentStep(STEPS.METADATA);
  };

  const handleSubmit = async () => {
    if (!selectedClient) {
      setClientSearchError('Client selection is required');
      return;
    }

    setCreating(true);
    setCurrentStep(STEPS.PROCESSING);
    setError(null);

    const approval = {
      clientId: selectedClient,
      portfolioId: selectedPortfolio,
      programId: selectedProgram,
      project: charterData.project,
      scopes: charterData.scopes,
      tasks: charterData.tasks,
      risks: charterData.risks
    };

    try {
      const response = await projectService.createCharterEntities(approval);
      setCreating(false);
      setCurrentStep(STEPS.COMPLETE);
      sessionStorage.removeItem('aiCharterDraft');

      const responseData = response.data || response;
      const projectId = responseData.projectId || responseData.id;

      setTimeout(() => {
        history.push(`/projects/${projectId}/overview`);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create project');
      setCreating(false);
      setCurrentStep(STEPS.METADATA);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client?.value || null);
    setClientSearch('');
    setClientSearchError('');
    setClientResults([]);
  };

  return (
    <div className={Styles.Wrapper}>
      <div className={Styles.Container}>
        <span className={Styles.ContainerGlow}></span>
        <div className={Styles.ContainerInner}>
          {/* Header */}
          <div className={Styles.Header}>
            <h1 className={`${Styles.LinearGradientText} flex gap-2 align-items-center text-xl-700`}>
              <AiIcon/>
              Create AI</h1>
            <p className={`text-sm-400 subHeading-color`}>
              AI-powered extraction and project creation from project charter Document
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className={Styles.ErrorBanner}>
              <span className="text-sm-500">{error}</span>
            </div>
          )}

          {/* Content */}
          <div className={Styles.Content}>
            {currentStep === STEPS.UPLOAD && (
              <UploadStep
                fileInputRef={fileInputRef}
                onFileSelect={handleFileSelect}
                extracting={extracting}
                uploadProgress={uploadProgress}
              />
            )}

            {currentStep === STEPS.REVIEW && charterData && (
              <ReviewStep
                charterData={charterData}
                onUpdateField={updateField}
                validationErrors={validationErrors}
                dateWarnings={dateWarnings}
                onNext={handleNext}
                onBack={() => setCurrentStep(STEPS.UPLOAD)}
                primeOptions={primeOptions}
                handlePrimeSearch={handlePrimeSearch}
              />
            )}

            {currentStep === STEPS.METADATA && (
              <MetadataStep
                selectedClient={selectedClient}
                selectedPortfolio={selectedPortfolio}
                selectedProgram={selectedProgram}
                clientSearch={clientSearch}
                portfolioSearch={portfolioSearch}
                programSearch={programSearch}
                clientResults={clientResults}
                portfolioResults={portfolioResults}
                programResults={programResults}
                onClientSearchChange={setClientSearch}
                onPortfolioSearchChange={setPortfolioSearch}
                onProgramSearchChange={setProgramSearch}
                onClientSelect={handleClientSelect}
                onPortfolioSelect={setSelectedPortfolio}
                onProgramSelect={setSelectedProgram}
                clientSearchError={clientSearchError}
                onSubmit={handleSubmit}
                onBack={() => setCurrentStep(STEPS.REVIEW)}
                isLoading={creating}
              />
            )}

            {currentStep === STEPS.PROCESSING && <ProcessingStep />}
            {currentStep === STEPS.COMPLETE && <CompleteStep />}
          </div>
        </div>
      </div>
    </div>
  );
};

// Upload Step Component
const UploadStep = ({ fileInputRef, onFileSelect, extracting, uploadProgress }) => (
  <div className={Styles.UploadSection}>
    <div className={Styles.UploadArea} onClick={() => !extracting && fileInputRef.current?.click()}>
      {!extracting ? (
        <div className='flex flex-column align-items-center gap-2'>
          <CloudUpload size={40} color="#CA4D91" />
          <h3 className={`${Styles.LinearGradientText} text-lg-600`}>Upload Project Charter</h3>
          <p className={`${Styles.LinearGradientText} text-sm-400 subHeading-color`}>PDF format, maximum 10MB</p>
        </div>
      ) : (
        <>
          <div className={Styles.LoadingSpinner} />
          <h3 className="text-lg-600">Extracting Data...</h3>
          <div className={Styles.ProgressBar}>
            <div className={Styles.ProgressFill} style={{ width: `${uploadProgress}%` }} />
          </div>
          <p className="text-sm-400 subHeading-color">{uploadProgress}%</p>
        </>
      )}
    </div>
    <input
      ref={fileInputRef}
      type="file"
      accept=".pdf"
      onChange={onFileSelect}
      style={{ display: 'none' }}
    />
  </div>
);

// Review Step Component
const ReviewStep = ({ charterData, onUpdateField, validationErrors, dateWarnings, onNext, onBack, primeOptions, handlePrimeSearch }) => {
  return (
    <div className={Styles.ReviewSection}>
      {dateWarnings.length > 0 && (
        <div className={Styles.WarningBanner}>
          <div className='flex justify-content-start align-items-center mb-2'>
            <TriangleAlertIcon size={16} color="#EDB126" className="mr-2" />
            <p className="text-sm-500">
              <strong>Date Hierarchy Warnings:</strong>
            </p>
          </div>

          <ul className="text-sm-400">
            {dateWarnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <p className="text-sm-400 subHeading-color mb-4">
        Review and edit the extracted charter data below. Click on any field to edit its value.
      </p>

      <div className={Styles.AccordionWrapper}>
        <Accordion collapsible multiple>
          {/* Project */}
          {charterData.project && (
            <AccordionItem className={Styles.accordionItem} value="project">
              <AccordionHeader className={Styles.accordionHeader} icon={<File size={20} color="#342c82" />}>
                <span className={Styles.accordionHeaderTitle}>{charterData.project.name || 'Untitled Project'}</span>
                <span className="pill blue ml-2">
                  {charterData.project.progress ?? 0}% 
                </span>
              </AccordionHeader>
              <AccordionPanel>
                <ProjectFields
                  project={charterData.project}
                  onUpdateField={onUpdateField}
                  validationErrors={validationErrors}
                />
              </AccordionPanel>
            </AccordionItem>
          )}

          {/* Scopes Section */}
          {charterData.scopes && charterData.scopes.length > 0 && (
            <AccordionItem className={Styles.accordionItem} style={{ marginLeft: "2rem" }} value="scopes-section">
              <AccordionHeader className={Styles.accordionHeader} icon={<Milestone size={20} color="#342c82" />}>
                <span className={Styles.accordionHeaderTitle}>Scopes & Milestones</span>
                <span className="pill purple ml-2">{charterData.scopes.length}</span>
              </AccordionHeader>
              <AccordionPanel>
                <div className={Styles.ScopesWrapper}>
                  <Accordion collapsible multiple>
                    {charterData.scopes.map(scope => {
                      const scopeTasks = charterData.tasks?.filter(t => t.scopeTempId === scope.tempId) || [];
                      return (
                        <AccordionItem
                          key={scope.tempId}
                          className={Styles.scopeAccordionItem}
                          value={`scope-${scope.tempId}`}
                        >
                          <AccordionHeader className={Styles.scopeAccordionHeader}>
                            <div className={Styles.ScopeSummary}>
                              {scope.isMilestone && <Milestone size={16} color="#342c82" />}
                              <span className="text-sm-500">{scope.name || 'Untitled Scope'}</span>
                              <div className={Styles.ScopeSummaryMeta}>
                                <span className="pill purple">Tasks: {scopeTasks.length}</span>
                                <span className="pill blue">{scope.progress ?? 0}%</span>
                              </div>
                            </div>
                          </AccordionHeader>
                          <AccordionPanel>
                            <ScopeFields
                              scope={scope}
                              tasks={scopeTasks}
                              onUpdateField={onUpdateField}
                              validationErrors={validationErrors}
                            />
                          </AccordionPanel>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </div>
              </AccordionPanel>
            </AccordionItem>
          )}

          {/* Risks */}
          {charterData.risks && charterData.risks.length > 0 && (
            <AccordionItem className={Styles.accordionItem} style={{ marginLeft: "2rem" }} value="risks">
              <AccordionHeader className={Styles.accordionHeader} icon={<CircleAlert size={20} color="#d1424f" />}>
                <span className={Styles.accordionHeaderTitle}>Risks & Issues</span>
                <span className="pill purple ml-2">{charterData.risks.length}</span>
              </AccordionHeader>
              <AccordionPanel>
                <RisksFields
                  risks={charterData.risks}
                  onUpdateField={onUpdateField}
                  validationErrors={validationErrors}
                  charterData={charterData}
                  primeOptions={primeOptions}
                  handlePrimeSearch={handlePrimeSearch}
                />
              </AccordionPanel>
            </AccordionItem>
          )}
        </Accordion>
      </div>

      <div className={Styles.Actions}>
        <Button
          btnClass="d_btn d_btn_whiteMd me-10 text-sm-500"
          label="Back"
          clickHandler={onBack}
        />
        <Button
          btnClass={`d_btn d_btn_primaryMd text-sm-500 ${Styles.NextButton}`}
          label="Continue"
          clickHandler={onNext}
        />
      </div>
    </div>
  );
};

// Project Fields Component
const ProjectFields = ({ project, onUpdateField, validationErrors }) => (
  <div className={Styles.FieldsWrapper}>
    <EditableField
      label="Project Name"
      value={project.name}
      onChange={(val) => onUpdateField('project', null, 'name', val)}
      error={validationErrors.project?.name}
      required
    />
    <div className='flex row gx-2 align-items-center'>
      <div className='col-4'>
        <EditableField
          label="Reference ID"
          value={project.refId}
          onChange={(val) => onUpdateField('project', null, 'refId', val)}
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="Start Date"
          value={project.startDate}
          max={project.endDate}
          onChange={(val) => onUpdateField('project', null, 'startDate', val)}
          type="date"
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="End Date"
          value={project.endDate}
          min={project.startDate}
          onChange={(val) => onUpdateField('project', null, 'endDate', val)}
          type="date"
        />
      </div>
    </div>
    <EditableField
      label="Description"
      value={project.description}
      onChange={(val) => onUpdateField('project', null, 'description', val)}
      multiline
    />

    <ContactField
      label="Executive"
      value={project.executive}
      onChange={(val) => onUpdateField('project', null, 'executive', val)}
      isMultiple={false}
    />

    <ContactField
      label="Sponsor"
      value={project.sponsor}
      onChange={(val) => onUpdateField('project', null, 'sponsor', val)}
      isMultiple={false}
    />

    <ContactField
      label="Stakeholders"
      value={project.stakeholders}
      onChange={(val) => onUpdateField('project', null, 'stakeholders', val)}
      isMultiple={true}
    />
  </div>
);

// Scope Fields Component
const ScopeFields = ({ scope, tasks, onUpdateField, validationErrors }) => (
  <div className={Styles.FieldsWrapper}>
    <div className='flex row gx-2 align-items-center'>
      <div className={`col-${scope.isMilestone ? '10' : '9'}`}>
        <EditableField
          label="Scope Name"
          value={scope.name}
          onChange={(val) => onUpdateField('scope', scope.tempId, 'name', val)}
          error={validationErrors[`scope_${scope.tempId}`]?.name}
          required
        />
      </div>
      <div className={`mt-1 col-${scope.isMilestone ? '2' : '3'}`}>
        <ModalCheckbox
          label={scope.isMilestone ? "Milestone" : "Mark as Milestone"}
          checked={scope.isMilestone}
          setChecked={(val) => onUpdateField('scope', scope.tempId, 'isMilestone', val)}
        />
      </div>
    </div>
    <div className='flex row gx-2 align-items-center'>
      <div className='col-4'>
        <EditableField
          label="Reference ID"
          value={scope.refId}
          onChange={(val) => onUpdateField('scope', scope.tempId, 'refId', val)}
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="Start Date"
          value={scope.startDate}
          max={scope.endDate}
          onChange={(val) => onUpdateField('scope', scope.tempId, 'startDate', val)}
          type="date"
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="End Date"
          value={scope.endDate}
          min={scope.startDate}
          onChange={(val) => onUpdateField('scope', scope.tempId, 'endDate', val)}
          type="date"
        />
      </div>
    </div>
    <div className='flex row gx-2 align-items-center'>
      <div className='col-4'>
        <EditableField
          label="Phase"
          value={scope.phase}
          onChange={(val) => onUpdateField('scope', scope.tempId, 'phase', val)}
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="Status"
          value={scope.status ?? 0}
          onChange={(val) => onUpdateField('scope', scope.tempId, 'status', parseInt(val))}
          type="select"
          options={ScopeStatus}
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="Priority"
          value={scope.priority ?? 0}
          onChange={(val) => onUpdateField('scope', scope.tempId, 'priority', parseInt(val))}
          type="select"
          options={scopePriorityOptions}
        />
      </div>
    </div>


    <EditableField
      label="Description"
      value={scope.description}
      onChange={(val) => onUpdateField('scope', scope.tempId, 'description', val)}
      multiline
    />

    {tasks.length > 0 && (
      <div className={Styles.TasksSection}>
        <div className={Styles.TasksHeader}>
          <TaskIcon />
          <span className="text-md-600">Tasks ({tasks.length})</span>
        </div>

        <Accordion collapsible multiple>
          {tasks.map(task => (
            <AccordionItem
              key={task.tempId}
              className={Styles.taskAccordionItem}
              value={`task-${task.tempId}`}
            >
              <AccordionHeader className={Styles.taskAccordionHeader}>
                <div className={Styles.TaskSummary}>
                  <span className="text-sm-500">{task.name || 'Untitled Task'}</span>
                  <div className={Styles.TaskSummaryMeta}>
                    {task.isMilestone && <span className="pill purple">Milestone</span>}
                    {task.isCritical && <span className="pill AtRisk">Critical</span>}
                    <span className="pill blue" style={{ minWidth: "55px" }}>{task.progress ?? 0}%</span>
                  </div>
                </div>
              </AccordionHeader>
              <AccordionPanel>
                <TaskFieldsComponent
                  task={task}
                  onUpdateField={onUpdateField}
                  validationErrors={validationErrors}
                />
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    )}
  </div>
);

// Task Fields Component - Now only shows detailed fields
const TaskFieldsComponent = ({ task, onUpdateField, validationErrors }) => (
  <div className={Styles.TaskDetails}>
    <div className='flex row gx-2 align-items-center'>
      <div className='col-11'>
        <EditableField
          label="Task Name"
          value={task.name}
          onChange={(val) => onUpdateField('task', task.tempId, 'name', val)}
          error={validationErrors[`task_${task.tempId}`]?.name}
          required
        />
      </div>
      <div className='col-1'>
        <EditableField
          label="Progress"
          value={task.progress ?? 0}
          onChange={(val) => val <= 100 && val >= 0 && onUpdateField('task', task.tempId, 'progress', parseInt(val))}
          type="number"
          min="0"
          max="100"
          suffix="%"
        />
      </div>
    </div>
    <div className='flex row gx-2 align-items-center mb-3'>
      <div className={`col-${task.isMilestone ? '2' : '3'}`}>
        <ModalCheckbox
          label={task.isMilestone ? "Milestone" : "Mark as Milestone"}
          checked={task.isMilestone}
          setChecked={(val) => onUpdateField('task', task.tempId, 'isMilestone', val)}
        />
      </div>
      <div className={`col-${task.isCritical ? '2' : '3'}`}>
        <ModalCheckboxWarning
          label={task.isCritical ? "Critical" : "Mark as Critical"}
          checked={task.isCritical}
          setChecked={(val) => onUpdateField('task', task.tempId, 'isCritical', val)}
        />
      </div>
    </div>

    <div className='flex row gx-2 align-items-center'>
      <div className='col-4'>
        <EditableField
          label="Reference ID"
          value={task.refId}
          onChange={(val) => onUpdateField('task', task.tempId, 'refId', val)}
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="Start Date"
          value={task.startDate}
          max={task.endDate}
          onChange={(val) => onUpdateField('task', task.tempId, 'startDate', val)}
          type="date"
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="End Date"
          value={task.endDate}
          min={task.startDate}
          onChange={(val) => onUpdateField('task', task.tempId, 'endDate', val)}
          type="date"
        />
      </div>
    </div>
    <div className='flex row gx-2 align-items-center'>
      <div className='col-4'>
        <EditableField
          label="Estimated Cost"
          value={task.estimatedCost}
          onChange={(val) => onUpdateField('task', task.tempId, 'estimatedCost', parseFloat(val))}
          type="number"
          min="0"
          prefix={accountCurrencySymbol}
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="Estimated Effort"
          value={task.estimatedEffort}
          onChange={(val) => onUpdateField('task', task.tempId, 'estimatedEffort', parseFloat(val))}
          type="number"
          min="0"
          suffix=" hrs"
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="Completion Date"
          value={task.completionDate}
          onChange={(val) => onUpdateField('task', task.tempId, 'completionDate', val)}
          type="date"
        />
      </div>
    </div>
    <div className='flex row gx-2 align-items-center'>
      <div className='col-4'>
        <EditableField
          label="Phase"
          value={task.phase}
          onChange={(val) => onUpdateField('task', task.tempId, 'phase', val)}
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="Status"
          value={task.status ?? 0}
          onChange={(val) => onUpdateField('task', task.tempId, 'status', parseInt(val))}
          type="select"
          options={taskStatusOptions}
        />
      </div>
      <div className='col-4'>
        <EditableField
          label="Priority"
          value={task.priority ?? 0}
          onChange={(val) => onUpdateField('task', task.tempId, 'priority', parseInt(val))}
          type="select"
          options={taskPriorityOptions}
        />
      </div>
    </div>
    <EditableField
      label="Description"
      value={task.description}
      onChange={(val) => onUpdateField('task', task.tempId, 'description', val)}
      multiline
    />
  </div>
);

// Risks Fields Component
const RisksFields = ({ risks, onUpdateField, validationErrors, charterData, primeOptions, handlePrimeSearch }) => (
  <div className={Styles.FieldsWrapper}>
    <Accordion collapsible multiple>
      {risks.map(risk => (
        <AccordionItem
          key={risk.tempId}
          className={Styles.riskAccordionItem}
          value={`risk-${risk.tempId}`}
        >
          <AccordionHeader className={Styles.riskAccordionHeader}>
            <div className={Styles.RiskSummary}>
              <div className="d-flex w-50">
                <div className={`d-flex align-items-center gap-1 justify-content-between`}>
                  {risk.dateResolved && <div className="d-flex align-items-center"> <CircleCheck size={16} color="#5aa55a" /></div>}
                  {risk.isIssue && !risk.dateResolved && <TriangleAlert size={16} color="#d1424f" />}
                  <span className={`text-sm-500 ${!risk.dateResolved ? (risk.isIssue ? Styles.IssueText : '') : Styles.ResolvedText}`}>
                    {risk.name || 'Untitled Risk'}
                  </span>
                </div>
              </div>
              <div className={`${riskStyle.gapsBetweeen} d-flex w-50 justify-content-center`} >
                <div className={`${riskStyle.riskPills}`} style={{ gap: '20px' }}>
                  <div className={`${riskStyle.riskPill}`}>
                    <div className='flex row'>
                      <label className="text-xsm-600 grey10Color mb-1">Severity</label>
                      <span className={`pill ${riskStyle.riskPillOverride} ${getRiskSeverityColorClass(risk.severity)}`}>{getRiskSeverityStatus(risk.severity)}</span>
                    </div>
                  </div>
                  <div className={`${riskStyle.riskPill}`}>
                    <div className='flex row'>
                      <label className="text-xsm-600 grey10Color mb-1">Type</label>
                      <span className={`pill Completed ${riskStyle.riskPillOverride}`}>{getRiskType(risk.type)}</span>
                    </div>
                  </div>
                  <div className={`${riskStyle.riskPill}`}>
                    <div className='flex row'>
                      <label className="text-xsm-600 grey10Color mb-1">Status</label>
                      <span className={`pill Completed ${riskStyle.riskPillOverride}`}>{getRiskStatus(risk.status)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AccordionHeader>
          <AccordionPanel>
            <RiskFieldsComponent
              risk={risk}
              onUpdateField={onUpdateField}
              validationErrors={validationErrors}
              charterData={charterData}
              primeOptions={primeOptions}
              handlePrimeSearch={handlePrimeSearch}
            />
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);


// Risk Fields Component - Now only shows detailed fields
const RiskFieldsComponent = ({ risk, onUpdateField, validationErrors, charterData, primeOptions, handlePrimeSearch }) => (
  <div className={Styles.RiskDetails}>
    <div className='flex row gx-2 align-items-center'>
      <div className={risk.isIssue ? 'col-10' : 'col-9'}>
        <EditableField
          label="Name"
          value={risk.name}
          onChange={(val) => onUpdateField('risk', risk.tempId, 'name', val)}
          required
        />
      </div>
      <div className={`mt-1 col-${risk.isIssue ? '2' : '3'}`} >
        <ModalCheckboxWarning
          label={risk.isIssue ? "Is Issue" : "Mark as Issue"}
          checked={risk.isIssue}
          setChecked={(val) => onUpdateField('risk', risk.tempId, 'isIssue', val)}
        />
      </div>
    </div>
    <div className={Styles.FieldRow}>
      <EditableField
        label="Affected Scope"
        value={risk.linkedScopeId}
        onChange={(val) => onUpdateField('risk', risk.tempId, 'linkedScopeId', val)}
        type="select"
        options={
          [{ value: null, name: 'None' },
          ...(charterData.scopes || []).map(s => ({
            value: s.tempId,
            name: s.name || 'Untitled Scope'
          }))]}
      />
      {
        risk.linkedScopeId && (
          <EditableField
            label="Affected Task"
            value={risk.linkedTaskId}
            onChange={(val) => onUpdateField('risk', risk.tempId, 'linkedTaskId', val)}
            type="select"
            options={[
              { value: null, name: 'None' },
              ...(charterData.tasks || []).filter(t => t.scopeTempId === risk.linkedScopeId).map(t => ({
                value: t.tempId,
                name: t.name || 'Untitled Task'
              }))
            ]}
          />
        )
      }

    </div>
    <div className='flex row gx-2 align-items-center'>
      <div className='col-3'>
        <EditableField
          label="Prime"
          type='search'
          value={risk.prime}
          options={primeOptions}
          onSearch={handlePrimeSearch}
          onChange={(val) => onUpdateField('risk', risk.tempId, 'prime', val?.value ?? null)}
          creatableSearch={true}
        />
      </div>
      <div className='col-3'>
        <EditableField
          label="Date Identified"
          value={risk.dateIdentified}
          onChange={(val) => onUpdateField('risk', risk.tempId, 'dateIdentified', val)}
          type="date"
        />
      </div>
      <div className='col-3'>
        <EditableField
          label="Date Impact"
          value={risk.dateImpact}
          onChange={(val) => onUpdateField('risk', risk.tempId, 'dateImpact', val)}
          type="date"
        />
      </div>
      <div className='col-3'>
        <EditableField
          label="Date Resolved"
          value={risk.dateResolved}
          onChange={(val) => onUpdateField('risk', risk.tempId, 'dateResolved', val)}
          type="date"
        />
      </div>
    </div>
    <div className='flex row gx-2 align-items-center'>
      <div className='col-3'>
        <EditableField
          label="Category"
          value={risk.type ?? 0}
          onChange={(val) => onUpdateField('risk', risk.tempId, 'type', parseInt(val))}
          type="select"
          options={riskCategoryOptions}
        />
      </div>
      <div className='col-3'>
        <EditableField
          label="Status"
          value={risk.status ?? 0}
          onChange={(val) => onUpdateField('risk', risk.tempId, 'status', parseInt(val))}
          type="select"
          options={RiskStatusOptions}
        />
      </div>
      <div className='col-3'>
        <EditableField
          label="Probability"
          value={risk.probability ?? 0}
          onChange={(val) => onUpdateField('risk', risk.tempId, 'probability', parseInt(val))}
          type="select"
          options={riskProbability}
        />
      </div>
      <div className='col-3'>
        <EditableField
          label="Impact"
          value={risk.impact ?? 0}
          onChange={(val) => onUpdateField('risk', risk.tempId, 'impact', parseInt(val))}
          type="select"
          options={riskImpact}
        />
      </div>
    </div>
    <EditableField
      label="Description"
      value={risk.description}
      onChange={(val) => onUpdateField('risk', risk.tempId, 'description', val)}
      multiline
    />

    <EditableField
      label="Mitigation Plan"
      value={risk.mitigationPlan}
      onChange={(val) => onUpdateField('risk', risk.tempId, 'mitigationPlan', val)}
      multiline
    />
    <EditableField
      label="Contingency Plan"
      value={risk.contingencyPlan}
      onChange={(val) => onUpdateField('risk', risk.tempId, 'contingencyPlan', val)}
      multiline
    />
  </div>
);

// Metadata Step Component
const MetadataStep = ({
  selectedClient, selectedPortfolio, selectedProgram,
  clientSearch, portfolioSearch, programSearch,
  clientResults, portfolioResults, programResults,
  onClientSearchChange, onPortfolioSearchChange, onProgramSearchChange,
  onClientSelect, onPortfolioSelect, onProgramSelect,
  clientSearchError, onSubmit, onBack, isLoading
}) => (
  <div className={Styles.MetadataSection}>
    <p className="text-sm-400 subHeading-color mb-4">
      Select the organizational context for this project
    </p>

    <div className={Styles.MetadataFields}>
      <div className="col-12 mb-3">
        <EditableField
          label="Client"
          required={true}
          type='search'
          value={selectedClient}
          options={clientResults}
          onSearch={onClientSearchChange}
          onChange={onClientSelect}
        />
      </div>

      <div className="col-12 mb-3">
        <EditableField
          label="Portfolio"
          type='search'
          value={selectedPortfolio}
          options={portfolioResults}
          onSearch={onPortfolioSearchChange}
          onChange={(val) => onPortfolioSelect(val?.value || null)}
        />
      </div>
      <div className="col-12 mb-3">
        <EditableField
          label="Program"
          type='search'
          value={selectedProgram}
          options={programResults}
          onSearch={onProgramSearchChange}
          onChange={(val) => onProgramSelect(val?.value || null)}
        />
      </div>
    </div>

    <div className={Styles.Actions}>
      <Button
        btnClass="d_btn d_btn_whiteMd me-10 text-sm-500"
        label="Back"
        clickHandler={onBack}
      />
      <Button
        icon={<FilePlus2 size={18} />}
        btnClass={`d_btn d_btn_primaryMd text-sm-500 ${Styles.CreateProjectButton}`}
        label="Create Project"
        clickHandler={onSubmit}
        isLoading={isLoading}
      />
    </div>
  </div>
);

// Processing Step Component
const ProcessingStep = () => (
  <div className={Styles.ProcessingSection}>
    <div className={Styles.LoadingSpinner} />
    <h3 className="text-lg-600 mt-4">Creating Project...</h3>
    <p className="text-sm-400 subHeading-color">Setting up project structure</p>
  </div>
);

// Complete Step Component
const CompleteStep = () => (
  <div className={Styles.CompleteSection}>
    <div className='flex col gap-1 justify-content-center align-items-center  mt-4'>
    <h3 className="text-lg-600">Project Created Successfully</h3>
    <CheckCircleIcon color='#00a86b' />
    </div>
    <p className="text-sm-400 subHeading-color">Redirecting to project...</p>
  </div>
);

export default AiProjectCharter;